import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Badge } from 'antd';
import { ApiError, ApiErrorResponse } from '../common/ApiClient';
import { parseFieldPath, stringifyFieldPath } from '../common/utils/fieldPath';
import { AlternativeAlgorithmsEditTable } from './alternatives/AlternativeAlgorithmsEditTable';
import { KeysEditTable } from './keys/KeysEditTable';
import translations from './translations/DataObjectForm.translations.json';
import validationTranslations from './translations/validation.translations.json';
import {
  DataObjectContentSegmentKey,
  DataObjectFormDataType,
  DataObjectOutputType,
  DataObjectType,
  DataObjectView,
} from './types';
import {
  FC,
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutationErrors } from '../common/queryClient';
import {
  ActionButton,
  DetailLayout,
  InfoIconUrl,
  ObjectHeader,
  Padding,
} from '../common/components';
import {
  GetApiErrorMessage,
  ValidationMessagesMap,
} from '../common/utils/ValidationMessagesMap';
import { useMutateDataObjectQuery } from './query/useMutateDataObjectQuery';
import { DefaultValues, useFormContext, useWatch } from 'react-hook-form';
import {
  DataObjectSchema,
  DataObjectValidFormDataType,
  ValidationRuleTestValidationType,
} from './schema';
import {
  Form,
  FormContentSegmentOption,
  FormAlerts,
  FormSegmentLabel,
  FormSegments,
  useAlertContext,
  FormRef,
} from '../common/form';
import { DataObjectFormContent } from './DataObjectFormContent';
import { confirmationHeaders } from '../common/utils';
import { DataObjectDimensionTable } from './dimensions/DataObjectDimensionTable';
import { DataObjectIndicatorTable } from './indicators/DataObjectIndicatorTable';
import { DataObjectExcelActions } from './excel/DataObjectExcelActions';
import { DataSourcesFormTable } from './sources/DataSourcesFormTable';
import { EditLock, ObjectType } from '../lock';
import { AlgorithmsFormTable } from './algorithms/table/AlgorithmsFormTable';
import { DataObjectVersionLabel } from './DataObjectVersionLabel';
import { CalendarEdit } from './calendar/CalendarEdit';
import styled from 'styled-components';
import { FormSegmentTotalEntries } from '../common/form/segments/FormSegmentTotalElements';
import { getGroupId, replaceListObjectsId, segmentSubField } from './utils';
import { ValidationRulesEditTable } from './validation/ValidationRulesEditTable';
import { DataObjectStatusSelect } from './DataObjectStatusSelect';
import {
  DataObjectDescriptionVersionStore,
  DataObjectDescriptionVersionStoreProvider,
} from './DataObjectDescriptionVersionStore';
import { DeadlinesEditTable } from './deadlines/DeadlinesEditTable';
import { useDeadlineValidationErrors } from './useDeadlineValidationErrors';
import { AlgorithmTestCalculationType } from './algorithms/testRun/schema/types';
import { DataObjectFormTypeBadge } from './DataObjectFormTypeBadge';

const { addTranslations, t } = i18nNsFactory('data-object-form');
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

const DataObjectHeader = () => {
  const code = useWatch({ name: 'code' });
  const name = useWatch({ name: 'name' });

  return (
    <Padding>
      <ObjectHeader upperText={code} lowerText={name} />
    </Padding>
  );
};

interface DataObjectFormCommonProps {
  dataObjectView: DataObjectView;
  dataObjectDescriptionVersion: DataObjectFormDataType;
  onSuccess: (params?: { id: number; mode?: string }) => void | Promise<void>;
  onClickCancel: (params?: { id: number; mode?: string }) => void;
  breadCrumb: ReactElement;
  openTestCalculate: (params: AlgorithmTestCalculationType) => void;
  openTestValidate: (params: ValidationRuleTestValidationType) => void;
}

export type DataObjectFormProps = DataObjectFormCommonProps &
  (
    | {
        mode: 'create';
        id?: never;
        preSubmit?: never;
        newVersion?: boolean;
      }
    | {
        mode: 'edit';
        id: number;
        preSubmit: () => void;
        newVersion?: boolean;
      }
    | {
        mode: 'clone';
        id?: never;
        preSubmit?: never;
        newVersion: boolean;
      }
  );

export const DataObjectForm: FC<DataObjectFormProps> = (props) => {
  const {
    dataObjectView,
    mode,
    id,
    dataObjectDescriptionVersion,
    preSubmit,
    onSuccess,
    newVersion = false,
    openTestCalculate,
    openTestValidate,
    ...rest
  } = props;
  const { mutateAsync } = useMutateDataObjectQuery();
  const { resetAlert, setSuccess } = useAlertContext();
  const formRef: RefObject<FormRef<any> | null> = useRef(null);

  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const {
    mutationErrors: uploadErrors,
    setMutationErrors: setUploadErrors,
    clearMutationErrors: clearUploadErrors,
  } = useMutationErrors(translateMutationError);
  const {
    mutationErrors: algorithmErrors,
    setMutationErrors: setAlgorithmErrors,
    clearMutationErrors: clearAlgorithmErrors,
  } = useMutationErrors(translateMutationError);
  const {
    deadlineValidationErrors,
    setDeadlineValidationErrors,
    displayDeadlineValidationType,
  } = useDeadlineValidationErrors();

  const handleFinish = useCallback(
    (values: DataObjectValidFormDataType, confirm: boolean) => {
      preSubmit?.();
      resetAlert();
      clearMutationErrors();
      clearUploadErrors();
      if (mode === 'clone') {
        replaceListObjectsId(values.dimensions, null);
        replaceListObjectsId(values.indicators, null);
        if (!newVersion) {
          values.dataSources.forEach((dataSource) => {
            // Preserve groupSources if they exist, otherwise set id to null for new data sources
            if (!dataSource.groupSource) dataSource.id = null;
          });
        }
        replaceListObjectsId(values.validationRules, -1);
        replaceListObjectsId(values.alternativeAlgorithmSets, null);
        if (values.requiredKeys)
          replaceListObjectsId(values.requiredKeys, null);
      }
      return mutateAsync({
        id: mode === 'edit' ? id : undefined,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then(async (responseData) => {
          if (responseData) {
            if (Object.hasOwn(responseData, 'versionId')) {
              await onSuccess({ id: responseData.versionId, mode });
              if (mode !== 'edit') {
                setSuccess(t('alert.success.created'));
              }
            } else {
              throw new Error('Expecting "versionId" in response data.');
            }
          }
        })
        .catch((error: ApiErrorResponse) => {
          setMutationErrors(
            error.response?.data.errors.map((apiError) => {
              if (apiError.path) {
                const segments = parseFieldPath(apiError.path);

                if (
                  segments?.[0] === 'execution' &&
                  segments[1] === 'executionDeadlines'
                ) {
                  return {
                    ...apiError,
                    path: stringifyFieldPath(segments.slice(1)),
                  };
                }
              }

              return apiError;
            }) ?? [],
          );
          throw error;
        });
    },
    [
      preSubmit,
      resetAlert,
      clearMutationErrors,
      clearUploadErrors,
      mode,
      newVersion,
      mutateAsync,
      id,
      onSuccess,
      setSuccess,
      setMutationErrors,
    ],
  );

  return (
    <DataObjectDescriptionVersionStoreProvider
      initialData={{
        dataObjectDescriptionVersion,
        openTestCalculate,
        openTestValidate,
      }}
    >
      <Form
        schema={DataObjectSchema.validFormDataSchema}
        defaultValues={dataObjectDescriptionVersion}
        serverErrors={mutationErrors}
        onSubmit={handleFinish}
        formRef={formRef}
      >
        <DataObjectFormInner
          {...rest}
          dataObjectView={dataObjectView}
          dataObjectDescriptionVersion={dataObjectDescriptionVersion}
          id={id}
          mode={mode}
          mutationErrors={mutationErrors}
          algorithmErrors={algorithmErrors}
          setAlgorithmErrors={setAlgorithmErrors}
          clearAlgorithmErrors={clearAlgorithmErrors}
          setUploadErrors={setUploadErrors}
          uploadErrors={uploadErrors}
          setDeadlineValidationErrors={setDeadlineValidationErrors}
          deadlineValidationErrors={deadlineValidationErrors}
          displayDeadlineValidationType={displayDeadlineValidationType}
        />
      </Form>
    </DataObjectDescriptionVersionStoreProvider>
  );
};

export const DataObjectFormInner: FC<
  Pick<
    DataObjectFormProps,
    | 'breadCrumb'
    | 'dataObjectView'
    | 'dataObjectDescriptionVersion'
    | 'id'
    | 'mode'
    | 'onClickCancel'
  > & {
    mutationErrors: ValidationMessagesMap;
    setUploadErrors: (
      apiErrors: ApiError[],
      getErrorMessage?: GetApiErrorMessage,
    ) => void;
    uploadErrors: ValidationMessagesMap;
    setDeadlineValidationErrors: (
      apiErrors: ApiError[],
      getErrorMessage?: GetApiErrorMessage,
    ) => void;
    deadlineValidationErrors: ValidationMessagesMap;
    displayDeadlineValidationType: (name: string) => string;
    algorithmErrors: ValidationMessagesMap;
    setAlgorithmErrors: (
      apiErrors: ApiError[],
      getErrorMessage?: GetApiErrorMessage,
    ) => void;
    clearAlgorithmErrors: any;
  }
> = ({
  breadCrumb,
  dataObjectView,
  dataObjectDescriptionVersion,
  id,
  mode,
  onClickCancel,
  setUploadErrors,
  uploadErrors,
  setDeadlineValidationErrors,
  deadlineValidationErrors,
  displayDeadlineValidationType,
  algorithmErrors,
  setAlgorithmErrors,
  clearAlgorithmErrors,
}) => {
  const groupAndSubgroupId = dataObjectDescriptionVersion.groupAndSubgroupId;
  const isDraft = !id || dataObjectDescriptionVersion.statusCode === 'DRAFT';
  const isActive =
    Boolean(id) && dataObjectDescriptionVersion.statusCode === 'ACTIVE';
  const isInput =
    dataObjectDescriptionVersion.dataObjectType === DataObjectType.IN;
  const isIntermediate =
    dataObjectDescriptionVersion.outputTypeCode ===
    DataObjectOutputType.INTERMEDIATE;

  const { resetAlert, setSuccess } = useAlertContext();

  const [outputTypeCode, formType] = useWatch({
    name: ['outputTypeCode', 'formType'],
  });
  const groupLock = useMemo(() => {
    const groupId = getGroupId(groupAndSubgroupId);
    if (groupId) {
      return [
        {
          objectId: groupId,
          objectType: ObjectType.DataObjectGroup,
        },
      ];
    }
    return undefined;
  }, [groupAndSubgroupId]);

  const [, setDataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');
  const { reset: resetForm } = useFormContext<DataObjectValidFormDataType>();

  useEffect(() => {
    setDataObjectDescriptionVersion(dataObjectDescriptionVersion);
    resetForm(
      dataObjectDescriptionVersion as DefaultValues<DataObjectValidFormDataType>,
      {
        keepDirty: false,
      },
    );
  }, [
    setDataObjectDescriptionVersion,
    resetForm,
    dataObjectDescriptionVersion,
  ]);

  const [algorithmsCount, setAlgorithmsCount] = useState<number>(
    dataObjectDescriptionVersion.algorithmsCount,
  );

  const formSegments = useMemo(() => {
    const segments: FormContentSegmentOption<DataObjectContentSegmentKey>[] = [
      {
        value: 'dimensions',
        label: (
          <FormSegmentLabel
            segmentValue="dimensions"
            formFieldName="dimensions"
          >
            {t('segment-option.dimensions')}
          </FormSegmentLabel>
        ),
        children: (
          <DataObjectDimensionTable
            dataObjectView={dataObjectView}
            outputTypeCode={outputTypeCode}
            defaultStatusCode={dataObjectDescriptionVersion.statusCode}
          />
        ),
        formFieldName: 'dimensions',
      },
      {
        value: 'indicators',
        label: (
          <FormSegmentLabel
            segmentValue="indicators"
            formFieldName="indicators"
          >
            {t('segment-option.indicators')}
          </FormSegmentLabel>
        ),
        children: (
          <DataObjectIndicatorTable
            dataObjectView={dataObjectView}
            defaultStatusCode={dataObjectDescriptionVersion.statusCode}
          />
        ),
        formFieldName: 'indicators',
      },
    ];

    if (isInput) {
      segments.push({
        value: 'dataSources',
        label: (
          <FormSegmentLabel
            segmentValue="dataSources"
            formFieldName="dataSources"
          >
            {t('segment-option.dataSources')}
          </FormSegmentLabel>
        ),
        children: (
          <DataSourcesFormTable
            defaultSourceType={
              dataObjectDescriptionVersion?.dataSources?.[0]?.sourceType
            }
          />
        ),
        formFieldName: 'dataSources',
      });
    } else {
      if (!isIntermediate) {
        segments.push({
          value: 'validationRules',
          label: (
            <FormSegmentLabel
              segmentValue="validationRules"
              formFieldName="validationRules"
            >
              {t('segment-option.validationRules')}
            </FormSegmentLabel>
          ),
          children: <ValidationRulesEditTable />,
          formFieldName: 'validationRules',
        });
      }
    }

    segments.push({
      value: 'algorithms',
      label: (
        <FormSegmentLabel segmentValue="algorithms" formFieldName="algorithms">
          {t('segment-option.algorithms')}
          {typeof algorithmsCount !== 'undefined' && (
            <Badge count={algorithmsCount} />
          )}
        </FormSegmentLabel>
      ),
      children: (
        <AlgorithmsFormTable
          isClone={mode === 'clone'}
          onAlgorithmsCountChange={setAlgorithmsCount}
          setMutationErrors={setAlgorithmErrors}
          clearMutationErrors={clearAlgorithmErrors}
        />
      ),
      count: algorithmsCount,
    });

    if (!isInput) {
      segments.push({
        value: 'alternativeAlgorithmSets',
        label: (
          <FormSegmentLabel
            segmentValue="alternativeAlgorithmSets"
            formFieldName="alternativeAlgorithmSets"
          >
            {t('segment-option.alternativeAlgorithmSets')}
          </FormSegmentLabel>
        ),
        children: <AlternativeAlgorithmsEditTable />,
        formFieldName: 'alternativeAlgorithmSets',
      });

      if (outputTypeCode === DataObjectOutputType.REPORT) {
        segments.push({
          value: 'requiredKeys',
          label: (
            <FormSegmentLabel
              segmentValue="requiredKeys"
              formFieldName="requiredKeys"
            >
              {t('segment-option.requiredKeys')}
            </FormSegmentLabel>
          ),
          children: <KeysEditTable />,
          formFieldName: 'requiredKeys',
        });
      }
    }

    if (outputTypeCode !== DataObjectOutputType.INTERMEDIATE) {
      segments.push({
        value: 'execution',
        label: (
          <FormSegmentLabel
            segmentValue="execution"
            formFieldName={[
              'execution.executionCalendar',
              'execution.executionPeriods',
              'execution',
            ]}
          >
            {t('segment-option.execution')}
          </FormSegmentLabel>
        ),
        children: <CalendarEdit dataObjectView={dataObjectView} />,
        formFieldName: [
          'execution.executionCalendar',
          'execution.executionPeriods',
        ],
      });

      segments.push({
        value: 'executionDeadlines',
        label: (
          <FormSegmentLabel
            segmentValue="executionDeadlines"
            formFieldName={['executionDeadlines']}
          >
            {t('segment-option.executionDeadlines')}
          </FormSegmentLabel>
        ),
        children: (
          <DeadlinesEditTable
            onDeadlineValidate={() => {
              setDeadlineValidationErrors([]);
              resetAlert();
            }}
            onDeadlineValidateErrors={(errors: ApiError[]) => {
              setDeadlineValidationErrors(errors);
            }}
            onDeadlineValidateSuccess={() => {
              setSuccess(t('alert.success.validated-deadlines'));
            }}
          />
        ),
        formFieldName: ['executionDeadlines'],
      });
    }

    return segments;
  }, [
    dataObjectView,
    outputTypeCode,
    dataObjectDescriptionVersion.statusCode,
    dataObjectDescriptionVersion?.dataSources,
    isInput,
    algorithmsCount,
    mode,
    setAlgorithmErrors,
    clearAlgorithmErrors,
    setDeadlineValidationErrors,
    resetAlert,
    setSuccess,
    isIntermediate,
  ]);

  const infoIconUrl = isInput
    ? InfoIconUrl.INPUT_DATA_OBJECT_DESCRIPTIONS_DETAILS
    : InfoIconUrl.OUTPUT_DATA_OBJECT_DESCRIPTIONS_DETAILS;

  return (
    <DetailLayout
      infoIconUrl={infoIconUrl}
      breadCrumb={breadCrumb}
      side={{
        header: <DataObjectHeader />,
        content: (
          <DataObjectFormContent dataObjectView={dataObjectView} mode={mode} />
        ),
        footer:
          (mode !== 'create' && (
            <EditLock
              id={id}
              entity={dataObjectDescriptionVersion}
              objectType={ObjectType.DataObjectDescriptionVersion}
              onTimeout={onClickCancel}
              additionalLocks={groupLock}
            />
          )) ||
          undefined,
      }}
      footerContent={<FormSegmentTotalEntries segments={formSegments} />}
      headerConfig={{
        actions: (
          <>
            {formType && <DataObjectFormTypeBadge formType={formType} />}
            {mode === 'edit' && (
              <DataObjectVersionLabel version={dataObjectDescriptionVersion} />
            )}
            {mode === 'edit' && <DataObjectStatusSelect />}
            <DataObjectExcelActions
              id={id}
              isDraft={isDraft}
              isActive={isActive}
              isInput={
                dataObjectDescriptionVersion.dataObjectType ===
                DataObjectType.IN
              }
              isReport={
                dataObjectDescriptionVersion.outputTypeCode ===
                DataObjectOutputType.REPORT
              }
              isIntermediate={
                dataObjectDescriptionVersion.outputTypeCode ===
                DataObjectOutputType.INTERMEDIATE
              }
              onError={setUploadErrors}
              mode={mode}
              dataObjectType={dataObjectDescriptionVersion.dataObjectType}
            />
            <ActionButton
              type="abort"
              onClick={() => onClickCancel(id ? { id } : void 0)}
            />
            <ActionButton type="formSubmit" />
          </>
        ),
      }}
      alert={
        <FormAlerts
          additionalErrors={[
            {
              errorAction: t('title.upload'),
              renderErrorHeading: renderExcelField,
              errors: uploadErrors,
            },
            {
              errorAction: t('deadlineValidation'),
              renderErrorHeading: displayDeadlineValidationType,
              errors: deadlineValidationErrors,
            },
            {
              errorAction: t('algorithmsDeletion'),
              errors: algorithmErrors,
            },
          ]}
        />
      }
    >
      <ContentWrapper>
        <FormSegments segments={formSegments} />
      </ContentWrapper>
    </DetailLayout>
  );
};

const ContentWrapper = styled(Padding)`
  width: 100%;
  min-width: fit-content;
  min-height: fit-content;
`;

function segmentField(path: string[]) {
  if (path.length === 3) {
    const index = Number.parseInt(path[1]);
    if (!Number.isNaN(index)) {
      return [
        t(`segment-option.${path[0]}`),
        index + 2, // 1-starting and excel header column
        t(`segments.${path[0]}.${path[2]}`),
      ].join(' / ');
    }
  }
  if (path.length === 4) {
    const index = Number.parseInt(path[2]);
    if (!Number.isNaN(index)) {
      return [
        t(`segment-option.${path[1]}`),
        index + 2, // 1-starting and excel header column
        t(`segments.${path[1]}.${path[3]}`),
      ].join(' / ');
    }
  } else if (path.length > 4) {
    return segmentSubField(path)
      .map((field) => t(field))
      .join(' / ');
  }

  return undefined;
}

function renderExcelField(name: string) {
  const path = name.split('.');

  if (path.length === 1) {
    return t([`fields.${name}.label`, `fields.${name}`]);
  }
  return segmentField(path) ?? name;
}
